// src/pages/YouTubePage.js
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';

const CHANNEL_URL = 'https://www.youtube.com/@oclubedoeu';

function formatViews(count) {
  const n = parseInt(count, 10);
  if (isNaN(n)) return '';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatDate(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return 'hoje';
  if (diffDays === 1) return '1 dia';
  if (diffDays < 7) return `${diffDays} dias`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} sem`;
  return `${Math.floor(diffDays / 30)} mes`;
}

function VideoModal({ videoId, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="yt-modal-overlay" onClick={onClose}>
      <div className="yt-modal-inner" onClick={e => e.stopPropagation()}>
        <button className="yt-modal-close" onClick={onClose} aria-label="Fechar">✕</button>
        <div className="yt-modal-iframe-wrap">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title="YouTube player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

function VideoRow({ video, onPlay, isLatest }) {
  return (
    <button className="yt-video-row" onClick={() => onPlay(video.id)}>
      <img
        className="yt-video-thumb"
        src={video.thumbnail || `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
        alt={video.title}
        loading="lazy"
      />
      <div className="yt-video-info">
        <div className="yt-video-title-row">
          <p className="yt-video-title">{video.title}</p>
          {isLatest && <span className="yt-novo-tag">NOVO</span>}
        </div>
        <p className="yt-video-meta">
          {formatViews(video.viewCount) && `${formatViews(video.viewCount)} views · `}
          {formatDate(video.publishedAt)}
        </p>
      </div>
    </button>
  );
}

function ShortCard({ video, onPlay, isLatest }) {
  return (
    <button className="yt-short-card" onClick={() => onPlay(video.id)}>
      <img
        className="yt-short-thumb"
        src={video.thumbnail || `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
        alt={video.title}
        loading="lazy"
      />
      <span className="yt-play-overlay">▶</span>
      {isLatest && <span className="yt-novo-tag yt-novo-tag--short">NOVO</span>}
    </button>
  );
}

export default function YouTubePage({ videos, shorts, playlists, markAsSeen, channelInfo = { avatar: '', name: 'Clube do Eu', subscribers: '0' } }) {
  const [videoFilter, setVideoFilter] = useState('todos');
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loadingPlaylist, setLoadingPlaylist] = useState(false);
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => { markAsSeen(); }, []); // eslint-disable-line

  const latestId = useMemo(() => {
    const all = [...videos, ...shorts].filter(v => v.publishedAt);
    if (!all.length) return null;
    return all.reduce((a, b) =>
      new Date(a.publishedAt) > new Date(b.publishedAt) ? a : b
    ).id;
  }, [videos, shorts]);

  async function handlePlaylistTap(playlist) {
    setLoadingPlaylist(true);
    setSelectedPlaylist({ ...playlist, videos: [] });
    try {
      const { data, error } = await supabase.functions.invoke('youtube-feed', {
        body: { playlistId: playlist.id },
      });
      if (!error && data?.playlistVideos) {
        setSelectedPlaylist({ ...playlist, videos: data.playlistVideos });
      }
    } catch {
      // erro silencioso
    } finally {
      setLoadingPlaylist(false);
    }
  }

  if (selectedPlaylist) {
    return (
      <div className="yt-page">
        {playingId && <VideoModal videoId={playingId} onClose={() => setPlayingId(null)} />}
        <div className="yt-playlist-header">
          <button className="yt-back-btn" onClick={() => setSelectedPlaylist(null)}>
            ← Voltar
          </button>
          <h2 className="yt-playlist-title">{selectedPlaylist.title}</h2>
        </div>
        {loadingPlaylist ? (
          <div className="yt-loading">Carregando vídeos...</div>
        ) : (
          <div className="yt-video-list">
            {selectedPlaylist.videos.map(v => (
              <VideoRow key={v.id} video={v} onPlay={setPlayingId} />
            ))}
            {selectedPlaylist.videos.length === 0 && (
              <p className="yt-empty">Nenhum vídeo encontrado.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="yt-page">
      {playingId && <VideoModal videoId={playingId} onClose={() => setPlayingId(null)} />}

      <div className="yt-channel-header">
        <div className="yt-channel-info">
          {channelInfo.avatar
            ? <img className="yt-channel-avatar-img" src={channelInfo.avatar} alt={channelInfo.name} />
            : <div className="yt-channel-avatar">C</div>
          }
          <div>
            <p className="yt-channel-name">{channelInfo.name}</p>
            {channelInfo.subscribers !== '0' && (
              <p className="yt-channel-subs">{formatViews(channelInfo.subscribers)} inscritos</p>
            )}
          </div>
        </div>
        <button
          className="yt-subscribe-btn"
          onClick={() => window.open(CHANNEL_URL, '_blank', 'noopener')}
        >
          Inscrever-se
        </button>
      </div>

      {shorts.length > 0 && (
        <section className="yt-section">
          <h3 className="yt-section-title">Shorts</h3>
          <div className="yt-shorts-carousel">
            {shorts.map(s => <ShortCard key={s.id} video={s} onPlay={setPlayingId} isLatest={s.id === latestId} />)}
          </div>
        </section>
      )}

      <section className="yt-section">
        <h3 className="yt-section-title">Vídeos</h3>
        <div className="yt-filter-chips">
          <button
            className={`yt-chip ${videoFilter === 'todos' ? 'active' : ''}`}
            onClick={() => setVideoFilter('todos')}
          >
            Todos
          </button>
          <button
            className={`yt-chip ${videoFilter === 'playlists' ? 'active' : ''}`}
            onClick={() => setVideoFilter('playlists')}
          >
            Por playlist
          </button>
        </div>

        {videoFilter === 'todos' && (
          <div className="yt-video-list">
            {videos.map(v => (
              <VideoRow key={v.id} video={v} onPlay={setPlayingId} isLatest={v.id === latestId} />
            ))}
            {videos.length === 0 && (
              <p className="yt-empty">Nenhum vídeo encontrado.<br />Configure a API key do YouTube para ver o conteúdo.</p>
            )}
          </div>
        )}

        {videoFilter === 'playlists' && (
          <div className="yt-playlist-list">
            {playlists.map(pl => (
              <button key={pl.id} className="yt-playlist-row" onClick={() => handlePlaylistTap(pl)}>
                <img
                  className="yt-playlist-thumb"
                  src={pl.thumbnail || ''}
                  alt={pl.title}
                  loading="lazy"
                />
                <div className="yt-playlist-info">
                  <p className="yt-playlist-name">{pl.title}</p>
                  <p className="yt-playlist-count">{pl.videoCount} vídeos</p>
                </div>
                <span className="yt-playlist-arrow">›</span>
              </button>
            ))}
            {playlists.length === 0 && (
              <p className="yt-empty">Nenhuma playlist encontrada.</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
